"use client";

// THE FILTER LINE, for any list that can be narrowed (John, 2026-10-25: "I
// don't like the view chips inside the modal … a dropdown filter would work
// better"). The source filter's grammar (components/activity/SourceFilter), for
// lists that are not sources: one quiet line, `showing everything ⌄`, an
// underlined word with a chevron that opens THE frosted menu (OptionsMenu:
// anchored on a desktop, the sheet on a phone). `everything` heads the list,
// each row says how many it would leave, and a quiet "show everything" stands
// beside the line while it narrows. Presentational: the parts and the one that
// is on come from the surface.

import { useRef, useState } from "react";
import OptionsMenu from "../os/artifact/OptionsMenu";
import type { MenuItem } from "../os/ContextMenu";
import { Chevron } from "../ui";
import "./activity.css";

type Anchor = { x: number; y: number; bottom: number };

export default function FilterLine({
  lead = "showing",
  options,
  total,
  on,
  onChange,
  title = "show",
}: {
  /** the word before the choice ("showing", "from") */
  lead?: string;
  options: ReadonlyArray<{ key: string; label: string; count: number }>;
  /** how many the list holds with nothing narrowed */
  total: number;
  /** the key that is on, or null for everything */
  on: string | null;
  onChange: (key: string | null) => void;
  /** the menu's title on a phone */
  title?: string;
}) {
  const btn = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const label = on === null ? "everything" : (options.find((o) => o.key === on)?.label ?? "everything");
  const toggle = () => {
    const r = btn.current?.getBoundingClientRect();
    setAnchor((a) => (a || !r ? null : { x: r.left, y: r.top, bottom: r.bottom }));
  };
  const choose = (key: string | null) => {
    setAnchor(null);
    if (key !== on) onChange(key);
  };
  const items: MenuItem[] = [
    { label: "everything", hint: String(total), selected: on === null, icon: on === null ? <span className="ink-dot" aria-hidden /> : undefined, onSelect: () => choose(null) },
    ...options.map((o, i) => ({
      label: o.label,
      hint: String(o.count),
      selected: on === o.key,
      separated: i === 0,
      icon: on === o.key ? <span className="ink-dot" aria-hidden /> : undefined,
      onSelect: () => choose(o.key),
    })),
  ];
  return (
    <div className="ac-filter">
      <button
        ref={btn}
        type="button"
        className="os-link ac-filter__by"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        data-on={on !== null || undefined}
        onClick={toggle}
      >
        <span className="os-link-text">
          {lead} <b>{label}</b>
        </span>
        <Chevron size={12} dir="down" />
      </button>
      {on !== null ? (
        <button type="button" className="quiet-link ac-filter__clear" onClick={() => choose(null)}>
          show everything
        </button>
      ) : null}
      <OptionsMenu open={anchor !== null} onClose={() => setAnchor(null)} items={items} anchor={anchor} title={title} />
    </div>
  );
}
